import React, { useCallback, useMemo } from 'react';
import { Linking } from 'react-native';

import { IconCartFill, IconCatering, IconDineIn, IconDrive } from '@fhs/ui';
import { SwitchServiceModeDialog } from '@fhs-legacy/frontend/src/components/switch-service-mode-dialog';
import { IRestaurantNode } from '@fhs-legacy/frontend/src/generated/rbi-graphql';
import { useServiceModeStatus } from '@fhs-legacy/frontend/src/hooks/use-service-mode-status';
import { useFlag } from '@fhs-legacy/frontend/src/utils/launchdarkly';
import { getDirectionsAppUri } from '@fhs-legacy/frontend/src/utils/native-actions';

import { getAddressLines } from '../../utils/get-address-lines';
import { useStoreCardOrderAction } from '../store-card/hooks/use-store-card-order-action';
import { useStoreCardStatus } from '../store-card/hooks/use-store-card-status';

import { useWeeklyHours } from './hooks/use-weekly-hours';
import { StoreDetailsView } from './store-details.view';
import type { StoreDetailsBadge, StoreDetailsService } from './types';

interface StoreDetailsProps {
  /** The store to show, once the locator has resolved it. */
  restaurant?: IRestaurantNode;
  isLoading: boolean;
  onClose: () => void;
}

/**
 * Store Details as a pane of the locator rather than its own route, so the map stays mounted
 * behind it. The caller decides how it is presented — in place of the list panel on desktop,
 * as a sheet over the locator below it.
 */
export const StoreDetails: React.VFC<StoreDetailsProps> = ({ restaurant, isLoading, onClose }) => {
  if (!restaurant) {
    return (
      <StoreDetailsView
        addressLines={[]}
        hoursRows={[]}
        isCtaDisabled
        isCtaLoading={false}
        isLoading={isLoading}
        onClose={onClose}
        services={[]}
        shouldFocusOnOpen
      />
    );
  }

  // Remounted per store so the order action and its dialogs never carry over to another one.
  return (
    <ResolvedStoreDetails
      key={restaurant._id ?? undefined}
      restaurant={restaurant}
      onClose={onClose}
    />
  );
};

interface ResolvedStoreDetailsProps {
  restaurant: IRestaurantNode;
  onClose: () => void;
}

const ResolvedStoreDetails: React.VFC<ResolvedStoreDetailsProps> = ({ restaurant, onClose }) => {
  const enableFutureOrdering = useFlag('enable-future-ordering');

  const status = useStoreCardStatus(restaurant);
  const { serviceModeStatus } = useServiceModeStatus(restaurant);
  const hoursRows = useWeeklyHours(restaurant.diningRoomHours);

  const {
    orderAction,
    unavailableMessage,
    isDisabled,
    storeClosedActionSheet,
    showChangeToPickUpDialog,
    dismissChangeToPickUpDialog,
    confirmChangeToPickUp,
  } = useStoreCardOrderAction(restaurant);

  const phoneDigits = restaurant.phoneNumber?.replace(/\D/g, '');
  const onCallPress = useCallback(() => {
    if (phoneDigits) {
      void Linking.openURL(`tel:${phoneDigits}`);
    }
  }, [phoneDigits]);

  const { latitude, longitude } = restaurant;
  const onDirectionsPress = useCallback(() => {
    if (latitude != null && longitude != null) {
      void Linking.openURL(getDirectionsAppUri(latitude, longitude));
    }
  }, [latitude, longitude]);

  // What the store offers, rather than what is open right now — the hours below already say
  // when, and a store shouldn't drop Catering from its profile outside catering hours.
  const services = useMemo<StoreDetailsService[]>(() => {
    const candidates: [boolean, StoreDetailsService][] = [
      [serviceModeStatus.TAKEOUT.capable, { key: 'pickup', label: 'Pickup', Icon: IconCartFill }],
      [serviceModeStatus.DELIVERY.capable, { key: 'delivery', label: 'Delivery', Icon: IconDrive }],
      [
        serviceModeStatus.CATERING_PICKUP.capable || serviceModeStatus.CATERING_DELIVERY.capable,
        { key: 'catering', label: 'Catering', Icon: IconCatering },
      ],
      [serviceModeStatus.EAT_IN.capable, { key: 'dineIn', label: 'Dine-in', Icon: IconDineIn }],
    ];

    return candidates.filter(([isOffered]) => isOffered).map(([, service]) => service);
  }, [serviceModeStatus]);

  const addressLines = useMemo(
    () => getAddressLines(restaurant.physicalAddress),
    [restaurant.physicalAddress]
  );

  // Same precedence as the store card: being unable to order at all outranks closing soon.
  const badge: StoreDetailsBadge | undefined = unavailableMessage
    ? { label: unavailableMessage, tone: 'error' }
    : status.badge
      ? { label: status.badge, tone: 'warning' }
      : undefined;

  const isClosed = !status.isOpen;
  const canScheduleOrder =
    isClosed && enableFutureOrdering && status.supportsMobileOrdering && !unavailableMessage;
  const ctaLabel = canScheduleOrder ? 'Schedule Order' : 'Order Here';

  return (
    <>
      <StoreDetailsView
        addressLines={addressLines}
        badge={badge}
        ctaAccessibilityLabel={`${ctaLabel} at ${restaurant.name ?? ''}`}
        ctaLabel={orderAction ? ctaLabel : undefined}
        hoursRows={hoursRows}
        hoursText={status.hoursText}
        isCtaDisabled={
          isDisabled || !status.supportsMobileOrdering || (isClosed && !enableFutureOrdering)
        }
        isCtaLoading={Boolean(orderAction?.loading)}
        isLoading={false}
        name={restaurant.name}
        onCallPress={phoneDigits ? onCallPress : undefined}
        onClose={onClose}
        onCtaPress={orderAction?.onClick}
        onDirectionsPress={latitude != null && longitude != null ? onDirectionsPress : undefined}
        services={services}
        shouldFocusOnOpen
        statusText={status.statusText}
      />

      {storeClosedActionSheet}

      {showChangeToPickUpDialog && (
        <SwitchServiceModeDialog
          showDialog
          onConfirm={confirmChangeToPickUp}
          onDismiss={dismissChangeToPickUpDialog}
        />
      )}
    </>
  );
};
